import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { KeyIcon } from '@phosphor-icons/react/dist/csr/Key';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import { ApiError, NETWORK_ERROR_CODE, PAGE_EXPIRED_CODE, api } from '../../lib/api/client';
import styles from './ForgotPasswordScreen.module.css';

/**
 * "I forgot my password" — `POST /auth/password/forgot`, the first of the two
 * signed-out account-recovery screens (task 6). The second, which takes the
 * code the person receives and a new password, is a separate task.
 *
 * It renders OUTSIDE `AppShell`, the same way `LoginPage` and
 * `ChangePasswordScreen` do: nobody reaching this screen has a session, so
 * there is no nav to render and no shell to put it in. It owns its own
 * `<h1>` for the same reason those two screens do.
 *
 * ## The confirmation is unconditional — this is the whole point of the screen
 *
 * `api.requestPasswordReset` "always resolves" (see the client's own
 * comment): the API answers 202 whether or not the address has an account,
 * specifically so that no observable difference here could tell an agent
 * which addresses are registered. A screen that rendered one message for a
 * resolved call and a different one for a rejected `ApiError` would rebuild
 * that oracle in the browser — the server's effort spent for nothing.
 *
 * So: a resolved promise and a rejected `ApiError` that is NOT a transport
 * failure both end in exactly the same rendered confirmation. That covers
 * every code the API can send for this endpoint today, including
 * `validation_failed` (a malformed address) and `too_many_requests` (the
 * endpoint's own throttle) — a 429 here still shows the confirmation,
 * deliberately, for the identical reason a 422 does.
 *
 * ## The two things that ARE allowed to look different
 *
 * A transport failure is not a refusal from the API — it is proof no request
 * was ever answered, so there is nothing to equalise. Three shapes count,
 * and the third was Important 1 of the whole-branch review:
 *
 * - an `ApiError` whose `code` is `NETWORK_ERROR_CODE` (`status === 0`: the
 *   fetch itself never got a response);
 * - an `ApiError` whose `code` is `PAGE_EXPIRED_CODE` — a 419, the CSRF
 *   pipeline rejecting the request in middleware, BEFORE any account lookup
 *   is attempted (`client.ts`'s `request()` already retried it once with a
 *   fresh token; this is the retry surviving too). This does NOT reopen the
 *   enumeration oracle the confirmation exists to close: that oracle is
 *   about what an ACCOUNT LOOKUP found, and a 419 happens before one is ever
 *   run, so it is byte-identical whether the typed address exists or not.
 *   Reachable from exactly the tab this screen is reached from — a sign-in
 *   page left open past the session's CSRF lifetime — showing the
 *   confirmation here would tell the person a code is coming when the
 *   request never reached the controller at all. A 500 is the opposite case
 *   and stays equalised: only the mail-sending branch can raise one, so it
 *   is a real (if rare) attempt that the account-existence oracle still
 *   applies to;
 * - anything thrown that is NOT an `ApiError` at all — a fault in this
 *   component's own code, not a server refusal. That branch mirrors
 *   `LoginPage.tsx`'s handling exactly: the agent is told the form did
 *   nothing, and the fault is re-thrown rather than swallowed and disguised
 *   as a credential problem, because that would blame the person for a bug
 *   in this codebase and hide it from every error reporter.
 *
 * ## `onRequested` fires from a CLICK, never from the API outcome — load-bearing
 *
 * Round 1 review, Critical: `onRequested` used to be called the instant the
 * confirmation was decided, one line after `setRequested(true)`. React 18
 * batches a parent's reaction into the same commit as the state update that
 * triggered it, so a caller that swaps screens on `onRequested` — which is
 * exactly what it exists for — unmounts THIS component before the browser
 * ever paints the confirmation it just computed. The confirmation this
 * screen worked so hard to make identical for success and failure would be
 * rendered and then discarded, unseen by anyone, in the same frame.
 *
 * So: **this screen must never be unmounted as a reaction to the request
 * completing.** The person leaves it only by clicking something —
 * the confirmation's own "enter the code now" control (rendered once
 * `requested` is true, see the JSX below), or "Back to sign in"
 * (`onCancel`), at any time. `onRequested` is wired to the FORMER click
 * alone, not to `setRequested(true)`, not to the promise settling, and not
 * to any effect that runs after either. A future caller (this screen has no
 * router of its own to enforce it) must not attach `onRequested` to a
 * `useEffect` keyed on `requested` either — that reintroduces the exact
 * same same-commit unmount this paragraph exists to rule out.
 *
 * ## The address is never cleared
 *
 * On every path — the confirmation or the one real error — the typed address
 * stays in the field. A confirmation that wiped the field would make a retry
 * (a typo, a different address) a re-typing exercise, and on the error path
 * clearing it would look like the form forgot what was just submitted.
 */

export interface ForgotPasswordScreenProps {
  /**
   * Called with the trimmed address that was actually sent, and ONLY when
   * the person clicks the confirmation's own control. Never a consequence of
   * `api.requestPasswordReset` settling — see "`onRequested` fires from a
   * CLICK" above. The reset screen needs the address so the person is not
   * asked to retype it, and retyping it is also a chance to type it
   * differently by mistake.
   */
  onRequested: (email: string) => void;
  /** Returns to the login page without sending anything. */
  onCancel: () => void;
}

export function ForgotPasswordScreen({ onRequested, onCancel }: ForgotPasswordScreenProps) {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [missing, setMissing] = useState(false);

  /** The one real error this screen can show. `null` once it is resolved. */
  const [failure, setFailure] = useState<string | null>(null);
  /** The unconditional confirmation. Mutually exclusive with `failure`. */
  const [requested, setRequested] = useState(false);
  /**
   * The trimmed address a request was actually made for — captured at the
   * moment the confirmation is decided, not read live from `email`, so an
   * edit made to the field AFTER the confirmation appears (before the
   * person clicks through) cannot change what `onRequested` is eventually
   * called with.
   */
  const [sentEmail, setSentEmail] = useState('');

  const emailRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement, same reasoning as LoginPage: everything below can
    // throw, and a default-action submit on a form with no method is a GET
    // that would put the address in the URL, the history and the referrer.
    event.preventDefault();

    if (submitting) return;

    // An empty attempt still spends a slot of whatever budget this endpoint
    // has, for nothing. Mirrors LoginPage's identical guard.
    const trimmed = email.trim();
    if (trimmed === '') {
      setMissing(true);
      setFailure(t('forgotPassword.missingEmail'));
      setRequested(false);
      emailRef.current?.focus();
      return;
    }
    setMissing(false);

    setSubmitting(true);
    setFailure(null);
    setRequested(false);

    try {
      await api.requestPasswordReset(trimmed);
      // NOT `onRequested()` here — see the module docblock's "`onRequested`
      // fires from a CLICK" section. Only the confirmation's own control,
      // rendered below once `requested` is true, calls it.
      setSentEmail(trimmed);
      setRequested(true);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // A fault in our own code, not a refusal from the API. The agent has
        // to be told the form did nothing, and the fault must not be
        // disguised as anything about the address — so: the generic
        // sentence, and then let it go on rejecting. See LoginPage.tsx's
        // identical branch.
        setFailure(t('forgotPassword.unexpected'));
        throw error;
      }

      if (error.code === NETWORK_ERROR_CODE) {
        // The one genuine client-side problem: nothing was ever answered, so
        // there is nothing to equalise into the confirmation.
        setFailure(t('auth.unreachable'));
      } else if (error.code === PAGE_EXPIRED_CODE) {
        // Whole-branch review, Important 1: a 419 is the CSRF pipeline
        // refusing the request in middleware — no account lookup ever ran,
        // so there is no oracle to protect here, and showing the
        // confirmation would tell the person a code is coming when the
        // request never reached the controller. See the module docblock's
        // "two things that ARE allowed to look different" section.
        setFailure(t('auth.sessionExpired'));
      } else {
        // Every other ApiError — a 422, a 429, a 500 — shows the identical
        // confirmation a success would. See the module docblock: rendering
        // any of these differently rebuilds the account-existence oracle the
        // endpoint spent effort removing. Same non-firing rule as the
        // success path: `onRequested` still waits for the click.
        setSentEmail(trimmed);
        setRequested(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles['page']} data-testid="forgotPasswordPage">
      <form
        className={styles['card']}
        method="post"
        noValidate
        onSubmit={(event) => void handleSubmit(event)}
      >
        <h1 className={styles['title']}>{t('forgotPassword.title')}</h1>
        <p className={styles['intro']}>{t('forgotPassword.intro')}</p>

        {/*
          Both mounted from the first frame, EMPTY — same discipline as
          LoginPage: assistive technology announces a change inside a region
          it is already watching. `failure` and `requested` are mutually
          exclusive, so at most one of these ever has content at once.
        */}
        <div
          className={styles['alert']}
          role="alert"
          aria-atomic="true"
          data-testid="forgotPasswordAlert"
        >
          {failure === null ? null : <span className={styles['alertText']}>{failure}</span>}
        </div>

        <div
          className={styles['confirmation']}
          role="status"
          aria-atomic="true"
          data-testid="forgotPasswordConfirmation"
        >
          {requested ? (
            <>
              <span className={styles['confirmationText']}>{t('forgotPassword.confirmation')}</span>
              {/*
                The ONLY thing that calls `onRequested`. See the module
                docblock: the person leaves this screen by clicking
                something, never as a side effect of the request settling.
              */}
              <Button
                icon={<KeyIcon size={16} />}
                type="button"
                variant="secondary"
                className={styles['continue']}
                onClick={() => {
                  onRequested(sentEmail);
                }}
              >
                {t('forgotPassword.continue')}
              </Button>
            </>
          ) : null}
        </div>

        <Input
          ref={emailRef}
          label={t('forgotPassword.emailLabel')}
          width="fill"
          type="email"
          name="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          error={missing ? t('form.requiredField') : undefined}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<PaperPlaneTiltIcon size={16} />} type="submit" loading={submitting} className={styles['submit']}>
            {t('forgotPassword.submit')}
          </Button>

          <Button icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />} type="button" variant="ghost" onClick={onCancel}>
            {t('forgotPassword.cancel')}
          </Button>
        </div>
      </form>
    </main>
  );
}
